import React from 'react'
import { useRouteMatch, useParams } from 'react-router'

import {
  isDraftsRoute,
  isProjectRoute,
  useFlag,
  RouteParams,
  readStorageItems,
} from '@sketch/modules-common'
import { useSearch } from '@sketch/utils'

import {
  addCollectionToProject,
  createOptimisticCollection,
  removeSharesFromProject,
} from '../../../collections/operations/utils'

import {
  DragAndDroppedShareIds,
  DragAndDroppedProjectToShareIds,
  useShareListItemDragDrop,
} from '../../hooks/useShareDragAndDrop'
import {
  useCollectionSearchArgument,
  useHideCollectionsSetting,
} from '../../../collections/hooks'
import { useCreateProject } from '../../../projects/hooks/useCreateProject'
import { parseFilters } from '../../../projects/hooks/useProject'

import { Tooltip } from '@sketch/components'

import { DragWrapper } from './ShareListItemDrag.styles'

import { useStorageItemsFilters } from '../../../storageItems/hooks'
import { useSortingSettings } from '../../../shares/hooks/useSortSettings'
import { removeFromStorageItemsList } from '../../../storageItems/utils'

import {
  useApolloClient,
  GetStorageItemsDocument,
  GetStorageItemsQueryVariables,
  useGetProjectDetailsQuery,
} from '@sketch/gql-types'
import produce from 'immer'

interface ShareListItemDragProps {
  disabled?: boolean
  shareId: string
  projectId?: string
}

const ShareListItemDrag: React.FC<ShareListItemDragProps> = props => {
  const {
    children,
    disabled = false,
    shareId,
    projectId: projectIdentifier,
  } = props

  const isNestedProjectsOn = useFlag('nested-projects')
  const { setHideCollections } = useHideCollectionsSetting()
  const collectionSearch = useCollectionSearchArgument()

  const { searchDebounced: search } = useSearch()
  const { sort } = useSortingSettings()
  const { filters } = useStorageItemsFilters()

  const cache = useApolloClient()
  const { path } = useRouteMatch()

  const isProjectView = isProjectRoute(path)
  const isDraftView = isDraftsRoute(path)

  const isValidViewToCreateProject = isProjectView || isDraftView

  const { workspaceId } =
    useParams<RouteParams<'WORKSPACE_PROJECT' | 'WORKSPACE_DRAFTS'>>()

  const storageItemsQueryVariables = {
    projectIdentifier: projectIdentifier || '',
    search,
    ...parseFilters(filters, search),
    after: null,
    sharesSortOrder: sort,
  } satisfies GetStorageItemsQueryVariables

  // We need the project info for the optimistic response, this data it's already in cache
  const { data: currentProject } = useGetProjectDetailsQuery({
    variables: { projectIdentifier: projectIdentifier || '' },
    skip: !projectIdentifier,
  })

  // Generates a new folder name (New Folder, New Folder (2), etc), based on the
  // existing folders
  const generateFolderName = () => {
    if (!projectIdentifier) return 'New Folder'

    const data = readStorageItems(cache, storageItemsQueryVariables)

    if (!data?.project?.storageItems) return 'New Folder'

    const storageItems = data.project.storageItems.entries
    const biggestFolderNumber = Object.values(storageItems)
      .filter(
        ({ name, __typename }) =>
          __typename === 'Project' && name.startsWith('New Folder')
      )
      .map(({ name }) => {
        const match = name.match(/^New Folder(?: (\d+))?$/)
        return match ? parseInt(match[1] || '1', 10) : NaN
      })
      .filter(number => !isNaN(number))
      .reduce((max, current) => Math.max(max, current), 0)

    return biggestFolderNumber === 0
      ? 'New Folder'
      : `New Folder (${biggestFolderNumber + 1})`
  }

  const { createProject } = useCreateProject()

  const handleDroppedShareToCollection = ({
    draggedShareId,
    droppedShareId,
  }: DragAndDroppedShareIds) => {
    // We don't want to create collections when nested projects FF is enabled,
    // in this case we'll need to create a nested project with the 2 shares inside
    if (isNestedProjectsOn) {
      return
    }

    // we need a valid project id to create a collection by drag and drop
    if (!projectIdentifier) {
      return
    }

    if (draggedShareId === droppedShareId) {
      // If share was dropped on itself, ignore
      return
    }

    if (!isValidViewToCreateProject) {
      return
    }

    // We need to show collections in case they are hidden
    // As otherwise, we will not be able to display a collection placeholder
    // which is requesting to enter a name for the new collection
    // see https://linear.app/sketch/issue/SWEB-908/hidden-collections-conflict-with-the-draganddrop-functionality
    setHideCollections(false)

    const optimisticCollection = createOptimisticCollection({
      cache,
      shareIds: [draggedShareId, droppedShareId],
    })

    addCollectionToProject({
      cache,
      projectIdentifier,
      search: collectionSearch,
      collection: {
        __typename: 'Collection',
        ...optimisticCollection,
      },
    })

    removeSharesFromProject(cache, projectIdentifier, [
      { identifier: draggedShareId },
      { identifier: droppedShareId },
    ])
  }

  // Creates a new nested project with the project dragged and share dropped
  const handleDropProjectToShare = async ({
    draggedProjectId,
    droppedShareId,
  }: DragAndDroppedProjectToShareIds) => {
    // we need a valid project id to create a nested project
    if (!projectIdentifier) {
      return
    }

    if (!isValidViewToCreateProject) {
      return
    }

    const newFolderName = generateFolderName()

    // Create nested project with both share and project inside
    createProject({
      variables: {
        workspaceId,
        parentProjectIdentifier: projectIdentifier,
        name: newFolderName,
        description: '',
        storageItems: [
          { identifier: draggedProjectId, type: 'PROJECT' },
          { identifier: droppedShareId, type: 'SHARE' },
        ],
      },
      optimisticResponse: {
        __typename: 'RootMutationType',
        createWorkspaceProject: {
          __typename: 'CreateWorkspaceProjectResponse',
          project: {
            __typename: 'Project',
            archivedAt: null,
            deletedAt: null,
            description: null,
            userCanLeave: false,
            projectPrivacyIcon: 'WORKSPACE_MEMBERS',
            identifier: 'new-project-id',
            name: newFolderName,
            parentProjectIdentifier: projectIdentifier,
            pinnedByCurrentUserAt: null,
            projectType: 'STANDARD',
            parentProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: currentProject?.project?.name ?? '',
              nestedProjectsCount:
                currentProject?.project?.nestedProjectsCount ?? 0,
              sharesCount: (currentProject?.project?.sharesCount ?? 0) - 1,
            },
            topLevelProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: '',
              workspaceAccessLevel:
                currentProject?.project?.workspaceAccessLevel ?? 'NONE',
            },
            workspaceAccessLevel:
              currentProject?.project?.workspaceAccessLevel ?? 'NONE',
          },
        },
      },
      update: (cache, { data }) => {
        // The strategy is to create the new project as quickly as possible to
        // give a sense of speed (that's possible because we are using an
        // optimistic response), and after the mutation is completed, the
        // nested project will be filled with the real data.
        const storageItems = readStorageItems(cache, storageItemsQueryVariables)

        const newProject = data?.createWorkspaceProject.project

        const newStorageItems = produce(storageItems, draft => {
          if (
            !draft?.project?.storageItems ||
            !newProject ||
            newProject.__typename !== 'Project'
          ) {
            return
          }

          draft.project.storageItems.meta.nestedProjectsCount += 1

          draft.project.storageItems.entries.unshift({
            ...newProject,
            sharesCount: 1,
            nestedProjectsCount: 1,
            previews: [],
          })

          draft.project.storageItems.entries.sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        })

        cache.writeQuery({
          query: GetStorageItemsDocument,
          variables: storageItemsQueryVariables,
          data: newStorageItems,
        })

        // We also want to remove the storage items from the parent project
        removeFromStorageItemsList({
          cache,
          target: {
            __typename: 'Project',
            identifier: draggedProjectId,
          },
        })
        removeFromStorageItemsList({
          cache,
          target: {
            __typename: 'Share',
            identifier: droppedShareId,
          },
        })
      },
    })
  }

  // Creates a nested project with the dragged share and the dropped share
  const handleDropdShareToNestedProject = async ({
    draggedShareId,
    droppedShareId,
  }: DragAndDroppedShareIds) => {
    // we need a valid project id to create a nested project
    if (!projectIdentifier) {
      return
    }

    if (draggedShareId === droppedShareId) {
      // If share was dropped on itself, ignore
      return
    }

    if (!isValidViewToCreateProject) {
      return
    }

    const newFolderName = generateFolderName()

    // Create nested project with both shares inside
    createProject({
      variables: {
        workspaceId,
        parentProjectIdentifier: projectIdentifier,
        name: newFolderName,
        description: '',
        storageItems: [
          { identifier: draggedShareId, type: 'SHARE' },
          { identifier: droppedShareId, type: 'SHARE' },
        ],
      },
      optimisticResponse: {
        __typename: 'RootMutationType',
        createWorkspaceProject: {
          __typename: 'CreateWorkspaceProjectResponse',
          project: {
            __typename: 'Project',
            archivedAt: null,
            deletedAt: null,
            description: null,
            userCanLeave: false,
            projectPrivacyIcon: 'WORKSPACE_MEMBERS',
            identifier: 'new-project-id',
            name: newFolderName,
            parentProjectIdentifier: projectIdentifier,
            pinnedByCurrentUserAt: null,
            projectType: 'STANDARD',
            parentProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: currentProject?.project?.name ?? '',
              nestedProjectsCount:
                (currentProject?.project?.nestedProjectsCount ?? 0) + 1,
              sharesCount: (currentProject?.project?.sharesCount ?? 0) - 2,
            },
            topLevelProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: '',
              workspaceAccessLevel:
                currentProject?.project?.workspaceAccessLevel ?? 'NONE',
            },
            workspaceAccessLevel:
              currentProject?.project?.workspaceAccessLevel ?? 'NONE',
          },
        },
      },
      update: (cache, { data }) => {
        // The strategy is to create the new project as quickly as possible to
        // give a sense of speed (that's possible because we are using an
        // optimistic response), and after the mutation is completed, the
        // nested project will be filled with the real data.
        const storageItems = readStorageItems(cache, storageItemsQueryVariables)

        const newProject = data?.createWorkspaceProject.project

        const newStorageItems = produce(storageItems, draft => {
          if (
            !draft?.project?.storageItems ||
            !newProject ||
            newProject.__typename !== 'Project'
          ) {
            return
          }

          draft.project.storageItems.meta.nestedProjectsCount += 1

          draft.project.storageItems.entries.unshift({
            ...newProject,
            sharesCount: 2,
            nestedProjectsCount: 0,
            previews: [],
          })

          draft.project.storageItems.entries.sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        })

        cache.writeQuery({
          query: GetStorageItemsDocument,
          variables: storageItemsQueryVariables,
          data: newStorageItems,
        })

        // We also want to remove the shares from the parent project
        removeFromStorageItemsList({
          cache,
          target: {
            __typename: 'Share',
            identifier: draggedShareId,
          },
        })
        removeFromStorageItemsList({
          cache,
          target: {
            __typename: 'Share',
            identifier: droppedShareId,
          },
        })
      },
    })
  }

  const {
    isDraggedOver,
    isDraggedOverItself,
    isDroppableAreaEnabled,
    ...dragDropEvents
  } = useShareListItemDragDrop({
    disabled,
    shareId,
    onShareDropped: isNestedProjectsOn
      ? handleDropdShareToNestedProject
      : handleDroppedShareToCollection,
    onProjectDropped: handleDropProjectToShare,
  })

  const canCreateProjectByDragNDrop =
    isValidViewToCreateProject &&
    isDroppableAreaEnabled &&
    !!isDraggedOver &&
    !isDraggedOverItself

  return (
    <DragWrapper
      data-testid="drag-wrapper"
      $isDraggedOver={canCreateProjectByDragNDrop}
      {...dragDropEvents}
    >
      <Tooltip
        disabled={!canCreateProjectByDragNDrop}
        content={
          isNestedProjectsOn
            ? 'Create a new project'
            : 'Create a new collection'
        }
        visible={canCreateProjectByDragNDrop}
        placement="top"
      >
        {children}
      </Tooltip>
    </DragWrapper>
  )
}

export default ShareListItemDrag
